<template>
    <div class="ticimax-filter-row-wrapper">
        <a-popover
            :visible="selected"
            trigger="click"
            @visibleChange="(isVisible) => (isVisible ? onSelected() : onCanceled())"
            placement="left"
            :overlayClassName="'no-padding'"
        >
            <div slot="content">
                <SingleFilterEditor v-model="clone" :on-closed="onCanceled" :types="types" />
            </div>
            <div class="filter-row">
                <div class="filter-item" :class="{ active: selected }">
                    <div class="filter-remove">
                        <div class="control-button">
                            <md-button
                                class="md-icon-button md-mini"
                                @click="
                                    (e) => {
                                        e.stopPropagation();
                                        onRemoved(clone.id);
                                    }
                                "
                            >
                                <md-icon>delete_outline</md-icon>
                            </md-button>
                        </div>
                    </div>
                    <div class="filter-icon">
                        <FieldIconPreview :attribute="clone.attribute" />
                    </div>
                    <div
                        class="filter-item-text"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="`${$t(
                            `ticimax.attributes.${clone.attribute}`
                        )} ${getOperatorName()} '${getAttributeValue()}'`"
                    >
                        <span>
                            {{ $t(`ticimax.attributes.${clone.attribute}`) }}
                        </span>
                        {{ getOperatorName() }}
                        <span v-if="isPayloadRequired()">{{ getAttributeValue() }} </span>
                    </div>
                </div>
            </div>
        </a-popover>
        <a-select
            :value="operator"
            @change="onOperatorChanged"
            @click="
                (e) => {
                    e.stopPropagation();
                }
            "
            :class="{ hovered: groupSelectHovered }"
            @mouseenter="() => selectGroupHoverStateChanged(true)"
            @mouseleave="() => selectGroupHoverStateChanged(false)"
            v-if="addFilterMode ? showOperatorSelect : true"
        >
            <a-select-option value="or"> {{ $t("chatbotv2.or") }}</a-select-option>
            <a-select-option value="and"> {{ $t("chatbotv2.and") }}</a-select-option>
        </a-select>
        <div class="filter-row small" v-else>
            <a-popover
                v-model="attributePickerVisible"
                trigger="click"
                placement="left"
                :overlayClassName="'no-padding'"
            >
                <div slot="content">
                    <FieldPicker
                        :types="types"
                        :default-selected="null"
                        :pick="onAttributePicked"
                        :filter-mode="true"
                    />
                </div>
                <div class="add-filter-button">
                    <span class="material-icons"> add </span>
                    {{ $t("chatbotv2.addFilter") }}
                </div>
            </a-popover>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../../utils/session";
import _ from "lodash";
import SingleFilterEditor from "./SingleFilterEditor";
import { mapActions, mapState } from "vuex";
import FieldIconPreview from "./FieldIconPreview.vue";
import FieldPicker from "./FieldPicker.vue";

const log = log2("SingleFilter");
export default {
    name: "SingleFilter",
    components: {
        FieldIconPreview,
        SingleFilterEditor,
        FieldPicker,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                id: generateRandomId(),
                rule: "greater_or_equal",
                attribute: "email",
                value: 0,
            }),
        },
        operator: {
            type: String,
            required: true,
            default: "or",
        },
        onOperatorChanged: {
            type: Function,
            required: true,
        },
        enableOperatorChanged: {
            type: Boolean,
            required: true,
            default: true,
        },
        selected: {
            type: Boolean,
            required: true,
            default: false,
        },
        onSelected: {
            type: Function,
            required: true,
        },
        onCanceled: {
            type: Function,
            required: true,
        },
        onRemoved: {
            type: Function,
            required: true,
        },
        groupSelectHovered: {
            type: Boolean,
            required: true,
        },
        onGroupSelectHovered: {
            type: Function,
            required: true,
        },
        addFilterMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        showOperatorSelect: {
            type: Boolean,
            required: false,
            default: true,
        },
        onAttributePicked: {
            type: Function,
            required: false,
            default: () => {},
        },
        types: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            attributePickerVisible: false,
            updateModalVisible: false,
            clone: _.cloneDeep(this.value),
        };
    },
    computed: {
        ...mapState({}),
    },
    methods: {
        ...mapActions({}),
        isLocalizedAttribute(attribute) {
            return true;
        },
        selectGroupHoverStateChanged(hovered) {
            log("selectGroupHoverStateChanged", hovered);
            this.onGroupSelectHovered(hovered);
        },
        getAttributeValue() {
            log("getAttributeValue", this.clone.attribute);
            return this.clone.value;
        },
        getOperatorName() {
            return this.$t(`chatbotv2.filters.${this.clone.rule}`);
        },
        isPayloadRequired() {
            return true;
        },
    },
    mounted() {
        log("mounted", this.value);
    },
    watch: {
        clone: {
            handler(newValue) {
                this.$emit("input", newValue);
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.ticimax-filter-row-wrapper {
    grid-column-gap: 3px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 53px;
    width: 100%;

    .ant-select {
        height: 37px;
        max-width: 70px;
        width: 70px;

        &:hover {
            .ant-select-selection {
                border-color: #40a9ff !important;
                border-right-width: 1px !important;
                background: #40a9ff52 !important;
            }
        }

        &.hovered {
            .ant-select-selection {
                border-color: #40a9ff !important;
                border-right-width: 1px !important;
                background: #40a9ff52 !important;
            }
        }

        .ant-select-selection--single {
            height: 37px;
        }
    }
}

.filter-row {
    .filter-item {
        background: #f5f5f5;
        border: 1px solid #f5f5f5;
        cursor: pointer;
        align-items: center;
        border-radius: 4px;
        display: flex;
        padding: 8px 1px;
        position: relative;
        transition: background-color 0.3s, border 0.3s, opacity 0.3s;

        .filter-icon {
        }

        &.active {
            background: #006cff;
            border-color: #006cff;

            .filter-icon {
                color: #fff;
            }

            .filter-item-text {
                color: #fff;
            }

            &:hover {
                background: #006cff;
                border-color: #006cff;
            }
        }

        &:hover {
            background: transparent;
            border-color: #006cff;

            .filter-remove {
                opacity: 1;
            }
        }

        .filter-remove {
            opacity: 0;
            position: absolute;
            right: -8px;
            top: -14px;
            transition: opacity 0.3s;

            .md-button {
                background-color: #fff;
                align-items: center;
                border-radius: 100%;
                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                cursor: pointer;
                display: flex;
                justify-content: center;
                margin-bottom: 7px;
                transition: background-color 0.3s;
                height: 30px;
                width: 30px;
                min-height: 30px !important;
                min-width: 30px !important;

                .md-icon {
                    color: #006cff;
                }
            }
        }

        .filter-icon {
            align-items: center;
            color: #006cff;
            display: flex;
            font-size: 15px;
            justify-content: center;
            margin-right: 10px;
            transition: color 0.3s;
            width: 25px;
        }

        .filter-item-text {
            font-size: 16px;
            line-height: 20px;
            overflow: hidden !important;
            text-overflow: ellipsis;
            transition: color 0.3s;
            white-space: nowrap;
            -webkit-line-clamp: 1;

            span {
                font-weight: 600;
            }
        }
    }

    &.small {
        align-items: center;
        color: #006cff;
        cursor: pointer;
        display: flex;
        height: 38px;
        transition: all 0.3s;

        .add-filter-button {
            padding: 0 !important;
            border: unset !important;
            width: 100px !important;
        }

        span {
            font-weight: 600;
            font-size: 15px;
            line-height: 19px;
        }
    }
}
</style>
